import React, { Suspense } from 'react';
import { useProviderTools } from '@cotera/client/app/hooks/use-available-tools';
import { useWebhookTriggerInstallState } from '@cotera/client/app/v1/workflow/use-webhook-trigger-install-state';
import type {
  WebhookProviderCatalogEntry,
  WebhookInstallStatus,
} from '../types';
import { WebhookInstallStatusBanner } from './webhook-install-status-banner';
import {
  requiresWebhookProviderToolConnection,
  resolveWebhookToolProviderKey,
  shouldSuppressWebhookInstallError,
} from './webhook-provider-tool-connection';

const WebhookInstallStatusSectionInner: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  toolProviderKey: string;
  installStatus?: WebhookInstallStatus;
  installError?: { code?: string; message?: string } | null;
}> = ({ catalog, toolProviderKey, installStatus, installError }) => {
  const provider = useProviderTools(toolProviderKey);

  if (
    shouldSuppressWebhookInstallError({
      catalog,
      installStatus,
      provider,
    })
  ) {
    return null;
  }

  return (
    <WebhookInstallStatusBanner
      installStatus={installStatus}
      installError={installError}
    />
  );
};

const WebhookInstallStatusLive: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  triggerId: string;
  isDraft?: boolean;
  fallbackInstallStatus?: WebhookInstallStatus;
  toolProviderKey?: string;
}> = ({
  catalog,
  triggerId,
  isDraft,
  fallbackInstallStatus,
  toolProviderKey,
}) => {
  const installState = useWebhookTriggerInstallState({
    triggerId,
    isDraft,
    fallbackInstallStatus,
  });

  if (toolProviderKey !== undefined) {
    return (
      <WebhookInstallStatusSectionInner
        catalog={catalog}
        toolProviderKey={toolProviderKey}
        installStatus={installState.installStatus}
        installError={installState.installError}
      />
    );
  }

  return (
    <WebhookInstallStatusBanner
      installStatus={installState.installStatus}
      installError={installState.installError}
    />
  );
};

export const WebhookInstallStatusSection: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  triggerId?: string;
  isDraft?: boolean;
  installStatus?: WebhookInstallStatus;
  installError?: { code?: string; message?: string } | null;
}> = (props) => {
  const toolProviderKey = resolveWebhookToolProviderKey(props.catalog);
  const usesToolConnection =
    requiresWebhookProviderToolConnection(props.catalog) &&
    toolProviderKey !== null;

  if (props.triggerId !== undefined && props.triggerId !== '') {
    const live = (
      <WebhookInstallStatusLive
        catalog={props.catalog}
        triggerId={props.triggerId}
        isDraft={props.isDraft}
        fallbackInstallStatus={props.installStatus}
        toolProviderKey={usesToolConnection ? toolProviderKey : undefined}
      />
    );

    if (!usesToolConnection) {
      return live;
    }

    return <Suspense fallback={null}>{live}</Suspense>;
  }

  return (
    <WebhookInstallStatusBanner
      installStatus={props.installStatus}
      installError={props.installError}
    />
  );
};
